// =====================================================================
// AdminPermissions.jsx - Gestion des droits d'accès
// =====================================================================
// 2 onglets :
//   1. MODULES - l'éditeur de droits : on choisit QUI (rôle / groupe /
//      personnes), on voit ses modules, on retire ou on donne.
//   2. AUDIT   - journal des modifications de droits (lecture seule).
//
// 30-09-2026 : les onglets « Matrice Rôle × Permission » et « Overrides
// individuels » ont été SUPPRIMÉS. Ce n'était pas un nettoyage cosmétique :
// trois éditeurs pour la même donnée, c'était trois endroits où regarder
// quand un droit manquait, et trois endroits à tenir d'accord. L'écran
// « Modules » fait tout ce qu'ils faisaient :
//   - la matrice des rôles  -> on coche un RÔLE dans la colonne « Qui » ;
//   - les overrides         -> on coche une ou plusieurs PERSONNES.
// La différence est qu'il annonce la portée réelle avant d'écrire, ce que
// la matrice ne faisait pas : y décocher une case touchait des dizaines de
// personnes sans jamais le dire.
//
// L'onglet Audit reste : c'est un historique, pas un éditeur.
//
// Accès : SUPER_ADMIN uniquement (vérifié backend + frontend).
// =====================================================================

import { useState, useEffect } from 'react';
import { permissions as permsApi } from '../../services/api.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import PermissionModulesTab from './PermissionModulesTab.jsx';

export default function AdminPermissions() {
  const { user } = useAuth();
  const [tab, setTab] = useState('modules'); // 'modules' | 'audit'

  // Gate: doar SUPER_ADMIN poate vedea pagina
  if (user?.role !== 'SUPER_ADMIN') {
    return (
      <div style={{ padding: 40, color: '#991b1b', background: '#fee2e2', borderRadius: 8, margin: 20 }}>
        <h2 style={{ marginTop: 0 }}>Accès interdit</h2>
        <p>Cette page est réservée aux SUPER_ADMIN.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: 16, fontSize: 13 }}>
      <h1 style={{ marginTop: 0, marginBottom: 4 }}>Droits d'accès</h1>
      <p style={{ color: '#64748b', marginTop: 0, marginBottom: 16 }}>
        Qui a droit à quoi — par rôle, par groupe ou personne par personne — et le journal
        de toutes les modifications.
      </p>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '2px solid #e2e8f0', marginBottom: 16 }}>
        {[
          { id: 'modules', label: '🧩 Modules' },
          { id: 'audit', label: '📜 Journal d\'audit' },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderBottom: tab === t.id ? '2px solid #3b82f6' : '2px solid transparent',
              marginBottom: -2,
              background: 'transparent',
              cursor: 'pointer',
              fontWeight: tab === t.id ? 600 : 400,
              color: tab === t.id ? '#1e40af' : '#64748b',
              fontSize: 14,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'modules' && <PermissionModulesTab />}
      {tab === 'audit' && <AuditTab />}
    </div>
  );
}

// =====================================================================
// AUDIT - Journal des modifications de droits
// =====================================================================
function AuditTab() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterAction, setFilterAction] = useState('');

  useEffect(() => { reload(); }, [filterAction]);

  async function reload() {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (filterAction) params.action = filterAction;
      const d = await permsApi.getAudit(params);
      setEntries(d.entries || []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }

  if (loading) return <div>Chargement...</div>;
  if (error) return <div style={{ color: 'red' }}>Erreur: {error}</div>;

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <label style={{ marginRight: 8 }}>Filtrer par action :</label>
        <select value={filterAction} onChange={e => setFilterAction(e.target.value)} style={selectStyle}>
          <option value="">Toutes</option>
          <option value="USER_ROLE_CHANGED">Changement de rôle</option>
          <option value="ROLE_PERMISSION_GRANTED">Droit accordé à un rôle</option>
          <option value="ROLE_PERMISSION_REVOKED">Droit retiré à un rôle</option>
          <option value="USER_PERMISSION_OVERRIDE_CREATED">Dérogation créée</option>
          <option value="USER_PERMISSION_OVERRIDE_UPDATED">Dérogation modifiée</option>
          <option value="USER_PERMISSION_OVERRIDE_REMOVED">Dérogation supprimée</option>
          {/* Sans ces trois lignes, les opérations de l'onglet « Modules » —
              devenu l'éditeur unique — figurent bien dans le journal mais sont
              introuvables au filtre. « Pourquoi Untel a perdu ce droit ? » ne
              se répondrait qu'en faisant défiler tout. */}
          <option value="PERMISSION_BULK_APPLIED">Modules — opération en masse</option>
          <option value="PERMISSION_BULK_USER_CHANGED">Modules — effet sur une personne</option>
          <option value="GROUP_PERMISSIONS_UPDATED">Droits d'un groupe modifiés</option>
        </select>
        <span style={{ marginLeft: 12, color: '#94a3b8' }}>{entries.length} entrées</span>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead style={{ background: '#f8fafc' }}>
          <tr>
            <th style={thStyle}>Date</th>
            <th style={thStyle}>Acteur</th>
            <th style={thStyle}>Action</th>
            <th style={thStyle}>Entité</th>
            <th style={thStyle}>Cible</th>
            <th style={thStyle}>Détails</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(e => (
            <tr key={e.id} style={{ borderTop: '1px solid #f1f5f9' }}>
              <td style={{ padding: '6px 8px', color: '#475569', fontSize: 11 }}>
                {new Date(e.createdAt).toLocaleString('fr-BE')}
              </td>
              <td style={{ padding: '6px 8px' }}>{e.actor?.name || e.actorUserId}</td>
              <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: 11 }}>{e.action}</td>
              <td style={{ padding: '6px 8px', color: '#64748b' }}>{e.entityType}</td>
              {/* Pas d'identifiant technique dans la colonne lue par un
                  humain : on préfère la personne, puis les noms de rôles et de
                  groupes que l'entrée transporte, et on ne retombe sur
                  l'entité brute que s'il n'y a vraiment rien d'autre. */}
              <td style={{ padding: '6px 8px' }}>{auditTargetLabel(e)}</td>
              <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: 11, color: '#64748b', maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {auditDetailsText(e)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// =====================================================================
// Helpers
// =====================================================================
// Colonne « Cible » du journal : un nom, jamais un identifiant technique.
function auditTargetLabel(entry) {
  if (entry.targetUser?.name) return entry.targetUser.name;
  let details = entry.details;
  if (typeof details === 'string') {
    try { details = JSON.parse(details); } catch { details = null; }
  }
  if (details && Array.isArray(details.groupes) && details.groupes.length > 0) {
    return details.groupes.join(' · ');
  }
  // `role` (écriture sur un rôle depuis l'écran Modules) : la ligne porte le
  // libellé français, pas « OPERATOR:timesheets.read.all ».
  if (details && details.role) {
    return details.droit ? `${details.role} — ${details.droit}` : details.role;
  }
  // `groupName` (édition des droits d'un groupe), `name` (création /
  // suppression), `userName` (mouvement de membre) : trois écritures
  // existantes, trois champs différents — on les accepte tous plutôt que
  // d'afficher un identifiant technique faute d'avoir deviné le bon.
  if (details && (details.groupName || details.name || details.userName)) {
    return details.groupName || details.name || details.userName;
  }
  if (typeof entry.entityId === 'string' && entry.entityId.startsWith('bulk:')) {
    return entry.entityId.includes('REMOVE') ? 'Retrait en masse' : 'Ajout en masse';
  }
  return entry.entityId || '-';
}

// Les détails, sans les listes d'identifiants techniques. Les entrées de
// l'écran « Modules » transportent `permissionIds` et `groupIds` à côté des
// libellés lisibles (`droits`, `groupes`) : les afficher déversait
// « cmpmgcjiz004f134qqi94ctfj » dans une colonne lue par un humain, ce que
// la colonne « Cible » juste à gauche s'applique justement à éviter.
const CHAMPS_TECHNIQUES = ['permissionIds', 'groupIds', 'roleIds', 'userIds'];
function auditDetailsText(entry) {
  if (!entry.details) return '';
  let d = entry.details;
  if (typeof d === 'string') {
    try { d = JSON.parse(d); } catch { return d; }
  }
  if (!d || typeof d !== 'object') return String(entry.details);
  const propre = { ...d };
  for (const k of CHAMPS_TECHNIQUES) delete propre[k];
  return JSON.stringify(propre);
}

const thStyle = { padding: '8px', textAlign: 'left', fontWeight: 600, color: '#1e293b', borderBottom: '1px solid #e2e8f0' };
const selectStyle = { padding: '6px 8px', borderRadius: 4, border: '1px solid #cbd5e1', background: 'white', fontSize: 13 };
